<template>
    <main class="requests-page">

        <section class="requests-shell">
            <app-header @show-drawer="showDrawer" @logout="logout" heading="Password requests"
                subHeading="Review user password change requests and reset passwords manually." />
            <!-- HEADER -->
            <!-- <header class="page-header">
                <h1></h1>
                <p class="header-copy"></p>
            </header> -->

            <!-- FILTER SECTION -->
            <section class="filter-card">
                <div class="filter-header">
                    <div class="section-title">
                        <span class="section-icon">
                            <q-icon :size="$q.screen.lt.md ? 'md' : ''" name="list_alt" />
                        </span>
                        <div>
                            <h2>Requests</h2>
                        </div>
                    </div>

                    <q-btn size="lg" flat dense round icon="refresh" class="refresh-btn" :loading="isLoading"
                        @click="getRequests">
                        <q-tooltip>Refresh</q-tooltip>
                    </q-btn>
                </div>

                <div class="filter-grid">
                    <q-input v-model.trim="filters.search" outlined dense clearable
                        placeholder="Search user, dealer, email..." class="formField search-field">
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </q-input>

                    <q-select v-model="filters.status" outlined dense clearable emit-value map-options
                        label="All statuses" :options="statusOptions" class="formField" />
                </div>

                <div class="filter-footer">
                    <div class="record-count">
                        <q-icon :size="$q.screen.lt.md ? 'md' : ''" name="description" />
                        <span>{{ tableRows.length }} {{ tableRows.length === 1 ? 'Request' : 'Requests' }}</span>
                    </div>

                    <q-btn v-if="hasFilters" flat dense no-caps icon="filter_alt_off" label="Clear Filters"
                        class="clear-filter-btn" @click="clearFilters" />
                </div>
            </section>

            <!-- ================= Q-TABLE (desktop + mobile grid) ================= -->
            <section class="list-card">
                <q-table :data="tableRows" :columns="columns" row-key="id" flat dense :grid="isMobile"
                    :loading="isLoading" :filter="filters" :filter-method="applyFilters" :pagination.sync="pagination"
                    :rows-per-page-options="[10]" class="requests-q-table">
                    <!-- Loading state -->
                    <template v-if="isLoading" v-slot:loading>
                        <div class="loading-state">
                            <q-spinner size="40px" color="primary" />
                            <span>Loading requests...</span>
                        </div>
                    </template>

                    <!-- Empty state -->
                    <template v-if="!isLoading" v-slot:no-data>
                        <div class="empty-state">
                            <div class="empty-icon">
                                <q-icon name="lock_reset" size="38px" />
                            </div>
                            <h3>No password requests found</h3>
                            <p>There are no requests matching your filters.</p>
                        </div>
                    </template>

                    <!-- ===== DESKTOP ROW ===== -->
                    <template v-if="!isLoading" v-slot:body="props">
                        <q-tr :props="props">
                            <q-td key="requested" :props="props">
                                <div class="requested-cell">
                                    <strong>{{ formatDateTime(props.row.createdAt) }}</strong>
                                    <small>Updated {{ formatDate(props.row.updatedAt) }}</small>
                                </div>
                            </q-td>

                            <q-td key="user" :props="props">
                                <div class="user-cell">
                                    <strong>{{ props.row.fullName }}</strong>
                                    <small>{{ props.row.requestUserId }}</small>
                                </div>
                            </q-td>

                            <q-td key="dealer" :props="props">
                                <span class="normal-value">{{ props.row.dealerName }}</span>
                            </q-td>

                            <q-td key="email" :props="props">
                                <a class="email-link" :href="`mailto:${props.row.requestEmail}`">{{
                                    props.row.requestEmail }}</a>
                            </q-td>

                            <q-td key="note" :props="props">
                                <div class="note-cell">
                                    <span>{{ props.row.requestNote || '—' }}</span>
                                    <small>IP: {{ props.row.requestedIp }}</small>
                                </div>
                            </q-td>

                            <q-td key="status" :props="props">
                                <span class="status-badge" :class="statusClass(props.row.status)">{{ props.row.status
                                    }}</span>
                            </q-td>
                            <q-td key="newPassword" :props="props">

                                <q-input  v-model.trim="props.row.newPassword" outlined dense
                                    :type="props.row.isPwd ? 'password' : 'text'" label="Password"
                                    :error="props.row.isValidation && !props.row.newPassword" no-error-icon hide-bottom-space
                                    class="formField">
                                    <template v-slot:append><q-icon  :name="props.row.isPwd ? 'visibility_off' : 'visibility'"
                                            color="primary" class="cursor-pointer" @click="props.row.isPwd = !props.row.isPwd" /></template>
                                </q-input>
                            </q-td>

                            <q-td key="action" :props="props">
                                <div class="action-cell">
                                    <q-btn :disable="!userRole.complete" :loading="props.row.isApproveLoader" @click="postActiononRequest(props.row, 'approve')" no-caps
                                        class="approve-btn q-px-sm" label="Approve" dense color="primary-gradient" />
                                    <q-btn :disable="!userRole.reject" :loading="props.row.isRejectLoader" @click="postActiononRequest(props.row, 'reject')" no-caps label="Reject"
                                        class="q-px-sm" outline dense color="negative" />
                                </div>
                            </q-td>
                        </q-tr>
                    </template>

                    <!-- ===== MOBILE CARD ===== -->
                    <template v-if="!isLoading" v-slot:item="props">
                        <article class="request-card shadow-4 q-ma-sm">
                            <div class="request-card-header">
                                <div>
                                    <span class="request-label">REQUESTED</span>
                                    <strong class="request-date">{{ formatDateTime(props.row.createdAt) }}</strong>
                                </div>
                                <span class="status-badge" :class="statusClass(props.row.status)">{{ props.row.status
                                    }}</span>
                            </div>

                            <div class="card-details">
                                <div class="detail-item">
                                    <span>User</span>
                                    <strong>{{ props.row.userName }} ({{ props.row.requestUserId }})</strong>
                                </div>
                                <div class="detail-item">
                                    <span>Dealer</span>
                                    <strong>{{ props.row.dealerName }}</strong>
                                </div>
                                <div class="detail-item">
                                    <span>Email</span>
                                    <strong>{{ props.row.requestEmail }}</strong>
                                </div>
                                <div class="detail-item" v-if="props.row.note">
                                    <span>Note</span>
                                    <strong>{{ props.row.requestNote }}</strong>
                                </div>
                                <div class="detail-item">
                                    <span>Admin action</span>
                                    <strong>{{ formatDateTime(props.row.actionAt) }} · {{ props.row.actionBy }}</strong>
                                </div>
                                <q-td key="newPassword" :props="props">

                                    <q-input v-model.trim="props.row.newPassword" outlined dense
                                        :type="props.row.isPwd ? 'password' : 'text'" label="Password"
                                        :error="props.row.isValidation && !props.row.newPassword" no-error-icon hide-bottom-space
                                        class="formField">
                                        <template v-slot:append><q-icon :name="props.row.isPwd ? 'visibility_off' : 'visibility'"
                                                color="primary" class="cursor-pointer" 
                                                @click="props.row.isPwd = !props.row.isPwd" /></template>
                                    </q-input>
                                </q-td>

                                <q-td key="action" :props="props">
                                    <div class="action-cell">
                                        <q-btn :disable="!userRole.complete" :loading="props.row.isApproveLoader" @click="postActiononRequest(props.row, 'approve')" no-caps
                                            class="approve-btn q-px-sm" label="Approve" dense
                                            color="primary-gradient" />
                                        <q-btn :disable="!userRole.reject" :loading="props.row.isRejectLoader" @click="postActiononRequest(props.row, 'reject')" no-caps label="Reject"
                                            class="reject-btn q-px-sm" outline dense color="negative" />
                                    </div>
                                </q-td>
                            </div>
                        </article>
                    </template>
                </q-table>

                <!-- CUSTOM FOOTER -->
                <div v-if="!isLoading && tableRows.length"
                    :class="isMobile ? 'mobile-pagination q-mb-lg' : 'pagination-wrapper'">
                    <span v-if="!isMobile" class="pagination-info">
                        Showing {{ paginationStart + 1 }} - {{ paginationEnd }} of {{ tableRows.length }}
                    </span>

                    <q-pagination v-model="pagination.page" :max="totalPages" :max-pages="isMobile ? 5 : 6"
                        direction-links :boundary-links="!isMobile" :size="$q.screen.lt.md ? 'lg' : 'md'" />
                </div>
            </section>
        </section>
        <drawer ref="refDrawer" />
    </main>
</template>

<script>
    import axios from 'axios';
    import apis from '@/utilities';
    import appHeader from "@/views/components/header.vue"
    import Drawer from '../layout/drawer.vue';

    export default {
        name: 'PasswordRequests',
        components: {
            "app-header": appHeader,
            Drawer,
        },

        data() {
            return {
                isLoading: false,

                requests: [],

                filters: {
                    search: '',
                    status: '',
                },

                statusOptions: [
                    { label: 'Completed', value: 'COMPLETED' },
                    { label: 'Rejected', value: 'REJECTED' },
                    { label: 'Pending', value: 'PENDING' },
                ],

                pagination: {
                    page: 1,
                    rowsPerPage: 10,
                },

                columns: [
                    { name: 'requested', label: 'Requested', field: 'createdAt', align: 'left' },
                    { name: 'user', label: 'User', field: 'userName', align: 'left' },
                    { name: 'dealer', label: 'Dealer', field: 'dealerName', align: 'left' },
                    { name: 'email', label: 'Email', field: 'email', align: 'left' },
                    { name: 'note', label: 'Note', field: 'note', align: 'left' },
                    { name: 'status', label: 'Status', field: 'status', align: 'left' },
                    { name: 'newPassword', label: 'New Password', field: 'newPassword', align: 'left' },
                    { name: 'action', label: 'Admin Action', field: 'adminNote', align: 'left' },
                ],
                userRole:{}
            };
        },

        computed: {
            url() {
                return this.$store.getters.url;
            },

            isMobile() {
                return this.$q.screen.width < 1024;
            },

            hasFilters() {
                return Object.values(this.filters).some((value) => !!value);
            },

            tableRows() {
                return this.applyFilters(this.requests, this.filters);
            },

            totalPages() {
                return Math.max(1, Math.ceil(this.tableRows.length / this.pagination.rowsPerPage));
            },

            paginationStart() {
                if (!this.tableRows.length) return 0;
                return (this.pagination.page - 1) * this.pagination.rowsPerPage + 1;
            },

            paginationEnd() {
                return Math.min(this.pagination.page * this.pagination.rowsPerPage, this.tableRows.length);
            },
        },

        watch: {
            'filters.search'() {
                this.pagination.page = 1;
            },
            'filters.status'() {
                this.pagination.page = 1;
            },
            'pagination.page'(val) {
                if (val > this.totalPages) this.pagination.page = 1;
            },
        },

        mounted() {
            this.getRequests();
            this.setUserRole();
        },

        methods: {
            setUserRole() {
            const {
                menu: { permissions },
            } = this.$store.state;

            if (permissions) {
                permissions?.map((m) => {
                    const accesstype = m.code?.split('.');
                    this.userRole[accesstype[accesstype?.length - 1]] = true;
                });
                console.log('userRole: ', this.userRole);
            }
        },
            applyFilters(rows, terms) {
                let data = rows;

                const search = (terms.search || '').toLowerCase().trim();
                if (search) {
                    data = data.filter((item) => {
                        const values = [item.fullName, item.requestUserId, item.dealerName, item.dealerCode, item.email, item.requestNote, item.requestEmail, item.status];
                        return values.filter(Boolean).some((value) => String(value).toLowerCase().includes(search));
                    });
                }

                if (terms.status) {
                    data = data.filter((item) => item.status === terms.status);
                }

                return data;
            },

            async getRequests() {
                this.isLoading = true;
                this.requests = [];
                try {
                    const response = await axios.get(`${this.url}/${apis.PasswordResetList}`);
                    this.requests = response.data.data.map( d => ({...d,isApproveLoader:false,isRejectLoader:false ,isValidation:false, isPwd: true})) || [];
                } catch (err) {
                    this.$store.dispatch('triggerUnauthorizedError', err);
                    console.error(err);
                } finally {
                    this.isLoading = false;
                }
            },

            clearFilters() {
                this.filters = { search: '', status: '' };
                this.pagination.page = 1;
            },

            viewAdmin(row) {
                console.log('row: ', row);
                // Admin profile/detail navigation yahan
            },

            statusClass(status) {
                const key = String(status || '').toLowerCase();
                return `status-${key || 'default'}`;
            },

            formatDate(date) {
                if (!date) return 'N/A';
                const value = String(date).substring(0, 10);
                const parts = value.split('-');
                if (parts.length !== 3) return value;
                return `${parts[2]}/${parts[1]}/${parts[0]}`;
            },
            showDrawer() {
                if (this.$refs.refDrawer) {
                    this.$refs.refDrawer.toggleDrawer();
                }
            },
            formatDateTime(date) {
                if (!date) return 'N/A';
                const d = new Date(date);
                if (Number.isNaN(d.getTime())) return 'N/A';
                const day = String(d.getDate()).padStart(2, '0');
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const year = d.getFullYear();
                const hrs = String(d.getHours()).padStart(2, '0');
                const mins = String(d.getMinutes()).padStart(2, '0');
                return `${day}/${month}/${year}, ${hrs}:${mins}`;
            },
            logout() {
                sessionStorage.setItem('token', null);
                this.$store.commit('logOutHandler');
            },

            async postActiononRequest(data, action) {
                if (action === "approve" && !data.newPassword) {
                    data.isValidation = true
                    this.warningNotification('New Password Required')
                    return
                }
                const passwordResetRequest = {
                    id: data.id,
                    userId: null,
                    requestUserId: data.requestUserId,
                    requestEmail: data.requestEmail,
                    requestNote: data.requestNote,
                    status: action === 'approve' ? "COMPLETED" : "REJECTED",
                    requestedIp: data.requestedIp,
                    requestedUserAgent: data.requestedUserAgent,
                    adminNote: data.adminNote,
                    resolvedByUserId: data.resolvedByUserId,
                    resolvedAt: data.resolvedAt,
                    createdAt: data.createdAt,
                    updatedAt: data.updatedAt,
                    fullName: data.fullName,
                    newPassword: action === "approve" ? data.newPassword : null,

                };

                if(action == 'approve') data.isApproveLoader = true
                else data.isRejectLoader = true
                try {
                    const response = await axios.post(`${this.url}/${apis.ResolvePasswordReset}`, passwordResetRequest);
                    if (response.data.success && response.data.validationState === 0) {
                        this.getRequests()
                    }
                    this.globalNotification(response.data.message, response.data.validationState)
                } catch (err) {
                    this.$store.dispatch('triggerUnauthorizedError', err);
                    console.error(err);
                } finally {
                    this.isLoading = false;
                    data.isApproveLoader = false
                    data.isRejectLoader = false
                }
            }
        },
    };
</script>

<style lang="scss" scoped>
@import '../../styles/quasar.scss';

.requests-page {
    min-height: 100vh;
    padding: 0;
    color: $primary;
    text-align: left;
}

.requests-shell {
    max-width: 100%;
    margin: 0 auto;
}

.approve-btn {
    background: $primary-gradient;
}

/* ================= HEADER ================= */

.page-header {
    padding: 24px 18px 8px;
}

.page-header h1 {
    margin: 0;
    color: #1c2a2e;
    font-size: 28px;
    line-height: 1.2;
    font-weight: 500;
}

.header-copy {
    margin: 6px 0 0;
    color: #73858a;
    font-size: 13px;
}

/* ================= FILTER ================= */

.filter-card {
    padding: 20px 18px 15px;
    background: #fff;
    border-bottom: 1px solid #e5edef;
}

.filter-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
}

.section-title {
    display: flex;
    align-items: center;
    gap: 12px;
}

.section-icon {
    width: 38px;
    height: 38px;
    display: grid;
    place-items: center;
    color: $primary;
    background: #e7f2f4;
    border-radius: 10px;
}

.section-title h2 {
    margin: 0;
    color: $primary;
    font-size: 17px;
}

.refresh-btn {
    color: $primary;
}

.filter-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}

.formField :deep(.q-field__control) {
    background: #fff;
}

.filter-footer {
    min-height: 40px;
    margin-top: 12px;
    display: flex;
    align-items: center;
}

.record-count {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #73858a;
    font-size: 15px;
}

.record-count .q-icon {
    color: $primary;
}

.clear-filter-btn {
    margin-left: auto;
    color: $primary;
}

/* ================= LIST CARD WRAPPER ================= */

.list-card {
    background: #fff;
    overflow: hidden;
}

/* ================= Q-TABLE OVERRIDES ================= */

.requests-q-table {
    :deep(.q-table__card) {
        box-shadow: none;
        background: transparent;
    }

    :deep(.q-table__middle) {
        box-shadow: none;
    }

    :deep(table) {
        width: 100%;
        min-width: 1150px;
        border-collapse: collapse;
    }

    :deep(thead tr th) {
        height: 44px;
        padding: 0 16px;
        color: #8a9a9e;
        background: #fbfcfd;
        border-bottom: 1px solid #e3ebed;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.05em;
        text-align: left;
        text-transform: uppercase;
        white-space: nowrap;
    }

    :deep(tbody td) {
        padding: 14px 16px;
        color: #35525a;
        border-bottom: 1px solid #edf2f3;
        font-size: 13px;
        vertical-align: top;
    }

    :deep(tbody tr:hover) {
        background: #f9fbfb;
    }

    :deep(.q-table__grid-content) {
        padding: 0;
        display: block;
        background: #f5f8f9;
    }

    :deep(.q-table__grid-item) {
        padding: 0;
        margin-bottom: 12px;
        width: 100%;
    }
}

/* ================= SHARED CELL CONTENT ================= */

.requested-cell,
.user-cell,
.note-cell {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.action-cell {
    display: flex;
    flex-direction: row;
    gap: 6px;
}

.requested-cell strong,
.user-cell strong,
.action-cell strong {
    color: #35525a;
    font-size: 13px;
    font-weight: 600;
}

.requested-cell small,
.user-cell small,
.note-cell small,
.action-cell small {
    color: #87969a;
    font-size: 11px;
}

.note-cell span {
    color: #35525a;
    font-size: 13px;
}

.normal-value {
    color: #35525a;
}

.email-link {
    color: $primary;
    font-size: 13px;
    text-decoration: none;
}

.email-link:hover {
    text-decoration: underline;
}

.admin-link {
    color: $primary;
    text-decoration: none;
}

.admin-link:hover {
    text-decoration: underline;
}

.status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    white-space: nowrap;
    color: $primary;
    background: #e7f2f4;
}

.status-completed {
    color: #1c6ea4;
    background: #e3f1fb;
}

.status-rejected {
    color: #c45151;
    background: #fceeee;
}

.status-pending {
    color: #b4790f;
    background: #fdf1de;
}

/* ================= PAGINATION ================= */

.pagination-wrapper {
    min-height: 65px;
    padding: 12px 24px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 25px;
    border-top: 1px solid #edf2f3;
}

.pagination-info {
    margin-right: auto;
    color: #87969a;
    font-size: 12px;
}

.mobile-pagination {
    min-height: 60px;
    padding: 10px;
    display: flex;
    justify-content: center;
    background: transparent;
    margin-bottom: 20px !important;
}

/* ================= LOADING / EMPTY ================= */

.loading-state,
.empty-state {
    min-height: 260px;
    width: 100%;
    padding: 40px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.loading-state {
    gap: 12px;
    color: #73858a;
    font-size: 13px;
}

.empty-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 14px;
    display: grid;
    place-items: center;
    color: $primary;
    background: #e7f2f4;
    border-radius: 50%;
}

.empty-state h3 {
    margin: 0;
    color: $primary;
    font-size: 16px;
}

.empty-state p {
    margin: 6px 0 0;
    color: #819297;
    font-size: 13px;
}

/* ================= MOBILE CARD ================= */

.request-card {
    padding: 16px;
    background: #fff;
    border: 1px solid #e3ebed;
    border-radius: 12px;
}

.request-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding-bottom: 12px;
    border-bottom: 1px solid #edf2f3;
}

.request-label {
    display: block;
    margin-bottom: 3px;
    color: #8a9a9e;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
}

.request-date {
    color: $primary;
    font-size: 14px;
}

.card-details {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 12px;
}

.detail-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.detail-item span {
    color: #91a1a5;
    font-size: 11px;
}

.detail-item strong {
    color: rgba(0, 0, 0, 0.87);
    font-size: 13px;
    font-weight: 500;
}

/* ============================================================
   TABLET (>= 641px)
   ============================================================ */

@media (min-width: 641px) {
    .requests-page {
        padding: 24px 16px;
    }

    .page-header h1 {
        font-size: 32px;
    }

    .filter-card {
        padding: 25px 34px 18px;
    }

    .filter-header {
        margin-bottom: 22px;
    }

    .section-title h2 {
        font-size: 19px;
    }

    .action-cell {
        display: flex;
        flex-direction: row;
        justify-content: end;
        gap: 6px;
    }

}

@media (max-width: 641px) {
    .action-cell {
        width: 100% !important;
        display: flex;
        flex-direction: row;
        justify-content: end;
        gap: 6px;
    }
    .action-cell,.approve-btn {
        width: 100% !important;
    }
    .action-cell,.reject-btn {
        width: 100% !important;
    }
}

/* ============================================================
   DESKTOP (>= 1024px)
   ============================================================ */

@media (min-width: 1024px) {
    .filter-grid {
        grid-template-columns: 2fr 1fr;
    }

    .pagination-wrapper {
        justify-content: flex-end;
    }
}

@media (min-width: 1201px) {
    .requests-shell {
        max-width: 80vw;
        margin: 0 auto;
    }
}

.formField {
    max-height: 40px;
    min-width: 160px;
}

.formField :deep(.q-field__control) {
    height: 40px;
    max-height: 40px;
}

.formField :deep(.q-field__native) {
    max-height: 40px !important;
}
</style>